import { PromotionTypes, TPOStatusType } from 'utils/Enums';
import { SortType } from './pricingSimulator';
import { PublishStatus } from 'constants/tpo';

export type getAllPlansVariablesType = {
    search_keyword: string;
    limit: number;
    offset: number;
    sort_by: string;
    sorting_order: 'desc' | 'asc';
    user_id: string | null;
    plan_type: null;
    show_synced_plans: boolean;
    filters: Object;
};

type DimensionDropdownTypeAlt = {
    label: string;
    key: string;
    dimension: string;
};

export type PlanEditMetaType = {
    editingUser: string | null;
    sessionExpiresInSecs: number | null;
    startedAt: number | null;
    planId: null | number;
};

export type DimensionConfigType = {
    dim1Attribute: null | DimensionDropdownTypeAlt;
    dim2Attribute: null | DimensionDropdownTypeAlt;
    eventDim1Attribute: null | DimensionDropdownTypeAlt;
    eventDim1NestedAttribute?: null | DimensionDropdownTypeAlt;
    dimensionDropdownList: null | DropdownItem[];
    calendarColumnDropdownList: null | DropdownItem[];
};

export type KpiConfigType = {
    [key: string]: {
        pre: string;
        post: string;
        label: string;
        allowedDecimals: number;
        showByDefault: boolean;
        availableFor: string[];
        planLabel: string;
        allowSort: boolean;
    };
};

export type PlaybookKpiConfigType = {
    [key: string]: {
        pre: string;
        post: string;
        label: string;
        type: 'single' | 'range';
    };
};

export type PromoLibraryKpiConfigType = {
    [key: string]: PromoLibraryKpiConfigItemType;
};

export type PromoLibraryKpiConfigItemType = {
    pre: string;
    post: string;
    label: string;
    storageType: PromoLibraryKpiStorageType;
    type: PromoLibraryKpiType;
    isEditable: boolean;
    allowNegative: boolean;
    allowedDecimals: number;
    allowedValues: (number | string)[];
    minValue: number | null;
    maxValue: number | null;
    filterOperators: string[];
};
export type PromoLibraryKpiType = 'text' | 'integer' | 'boolean';
export type PromoLibraryKpiStorageType = 'select' | 'single' | 'range' | 'boolean';
export type EventViewType = 'calender' | 'promo_metrics' | 'plan_metrics' | 'plan_summary';
export type PlanSummarySubTab = 'ps_product_view' | 'ps_tactic_view';

export type DropdownItem = {
    label: string;
    value: string;
    dimension?: string;
};

export enum OptimizationPollingStatus {
    NOT_AVAILABLE = 'Not Available',
    IN_PROGRESS = 'in_progress',
    ERROR = 'ERROR',
    COMPLETED = 'COMPLETED',
    NULL = '',
}

export enum InfeasibilityPollingStatus {
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR',
}

export enum ValidatePlanPollingStatus {
    NOT_STARTED = '',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR',
}

export enum ValidatePromoLibraryPollingStatus {
    NOT_STARTED = '',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    ERROR = 'error',
}

export interface InfeasibilityConstraint {
    constraint_name: string;
    infeasible: boolean;
}

export interface InfeasibilityConstraintGroup {
    constraint_group: string;
    infeasible: boolean;
    constraints: InfeasibilityConstraint[];
}

export type SelectedPlanInfoType = {
    planId: number;
    planName: string;
    createdBy: string;
    updatedAt: string;
    status: TPOStatusType;
    dim1Filter: { label: string; value: string[] };
    dim2Filter: { label: string; value: string[] };
    offset?: number;
    count?: number;
    sortObject?: SortType;
    eventsData?: any[];
    error?: any;
    planYear: number;
    search?: string;
    isTpmPlan: boolean;
    tpm_last_sync_date: string;
    division?: string;
    divisionName?: string;
    enableOptimization: boolean;
    customerType: string;
    baselineVolumeVersion: string;
};

export type PromoEventKPIs = {
    metricConfig?: MetricConfig[]; // all available metric info
    spendConfig?: MetricConfig[]; // all available spend details info
};

export type OfferMethod = 'manual' | 'previous' | 'promo_library';

export type OfferTypeUnit = 'abs' | '%';

export type PromoEvent = {
    name: string;
    createdBy: string;
    CRAStartDate: number;
    CRAEndDate: number;
    displayACV: number;
    featureACV: number;
    // contract: string;
    //  newStatus: string;
    // offerMethod: OfferMethod;
    // offer: string | [string | number, string | number];
    // offerType?: string; // only applicable to manual offer method
    // offerTypeUnit?: OfferTypeUnit; // only applicable to manual offer method
    // CRADuration: number;
    // par: string;
    // promoGrouping: string;
    selectedDim1NestedAttributes?: string[];
    selectedDim1Attributes: string[];
    promoTacticsIdNamePair?: promoTacticIdNamePair[];
    currentPromoTactic: PromoTactic;
    firstPromoTacticBackup?: {
        promoInfo?: PromoTactic;
        promoId?: string | null;
    };
    is_ever_successfully_published?: boolean;
};

export type EventLibraryEvent = {
    name: string;
    createdBy: string;
    CRAStartDate: number;
    CRAEndDate: number;
    displayACV: number;
    featureACV: number;
    // contract: string;
    //  newStatus: string;
    // offerMethod: OfferMethod;
    // offer: string | [string | number, string | number];
    // offerType?: string; // only applicable to manual offer method
    // offerTypeUnit?: OfferTypeUnit; // only applicable to manual offer method
    // CRADuration: number;
    // par: string;
    // promoGrouping: string;
    selectedDim1NestedAttributes?: string[];
    selectedDim1Attributes: string[];
};

export type promoTacticIdNamePair = {
    name: string;
    id: string;
    promotionType: PromotionTypes | null;
};

export type Metric = {
    id: number;
    name: string;
    value: string;
    isSelected: boolean;
    recomValue?: string;
    isEditable?: boolean;
    isHidden: boolean;
};

export type PromoTactic = {
    [key: string]: any;
    name: string;
    promoId: string;
    tpmPromoId: number | null;
    status: string | null;
    customPromo: string | null;
    promoType: number | null;
    tacticType: number | null;
    promoStartDate: number | null; // ISO date string format
    promoEndDate: number | null; // ISO date string format
    ppgs: ProductPPG[];
    isLoading: boolean;
    featureQuality: number | null;
    displayQuality: number | null;
    offerType: string | null;
    manualOffer: string | null;
    buyGet: [number, number] | null;
    buyFor: [number, number] | null;
    buySave: [number, number] | null;
    discount: number | null;
    offer: number | null;
    craStartDate: number | null;
    craEndDate: number | null;
    comboUnit: 'abs' | '%' | 'item';
    buyForUnit: 'abs';
    discountUnit: 'abs' | '%';
    buySaveUnit: 'abs' | '%';
    hasChanges?: boolean;
    belowLUC?: boolean;
    belowMPIC?: boolean;
    isDataLoading?: boolean;
    newAddedPPgs?: Number[];
    deletedPPgs?: Number[];
    lastUpdatedAt?: number | null;
    isRefreshRequired?: boolean;
    contractNumber?: string | null;
    parNumber?: string | null;
    approvedDate?: number | null;
    fundingRequest?: string | null;
    ismCampaignId?: string | null;
    suggestedOfferType?: string | null;
    suggestedOffer?: string | null;
    publishStatus?: PublishStatus | null;
};

// ProductPPG type
export type ProductPPG = {
    id: number; // Unique identifier for the product
    name: string; // Name of the product
    isSelected: boolean; // Whether the product is selected based on the promo tactic
    metrics: Metric[];
    spendDetails: Metric[];
};

export type MetricConfig = {
    name: string;
    value: string;
    isSelected?: boolean;
    displayOrder: number;
};

export type PromoMetricType = {
    promo_metric_id: number;
    promo_metric_name: string;
    editable: boolean;
};

export type SpendMetricType = {
    spend_metric_id: number;
    spend_metric_name: string;
    editable: boolean;
};

export type TacticType = {
    tactic_id: number;
    tactic_name: string;
};

export type PromoType = {
    promotion_id: number;
    promotion_name: string;
    tactics: TacticType[];
    promo_metrics: PromoMetricType[];
    spend_metrics: SpendMetricType[];
};

export type eventStatusType = 'added' | 'updated' | 'deleted' | 'in_sync';

export type PromoConfigDictionary = {
    promotions: Record<number, string>; // No change for promotions
    promoMetrics: Record<number, ItemWithEditable>;
    spendMetrics: Record<number, ItemWithEditable>;
    tactics: Record<number, string>; // No change for tactics
    kpiDependencies: Record<string, string[]>;
    kpiFormulas: Record<string, string>;
    promoMetricMapping: Record<number, PromoMetricMapping>;
};

interface PromoMetricMapping {
    name: string;
    promoMetrics: Record<number, ItemWithEditable>;
    spendMetrics: Record<number, ItemWithEditable>;
}

export type ItemWithEditable = {
    id: number;
    name: string;
    name_key: string;
    editable: boolean;
    defaultShow: boolean;
    allowNegative: boolean;
    allowedDecimals: number;
    preUnit: string;
    postUnit: string;
    minValue: number | null;
    maxValue: number | null;
    orderId: number;
    groupName: string;
    isLumpsum: boolean;
    customerType: string;
    is_past_dates_editable?: boolean;
};

export type RbacType = {
    showCreatePlanButton: boolean;
    showApproveAndPublish: boolean;
};

export type AttributesPromoMapping = {
    [key: string]: string[];
};

export type PopUpErrorObject = {
    title: string;
    description: string[]; // list of error messages for this title
};

export type DivisionMetricMapping = {
    [key: number]: { spends: number[]; metrics: number[] };
};
